import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { addDays, format, isToday, subDays } from "date-fns";
import { ja } from "date-fns/locale";
import natsort from "natsort";
import Paper from "@mui/material/Paper";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@mui/icons-material";

import Layout from "../templates/Layout"; // Layoutコンポーネントをインポート
import { LuArrowUpDown } from "react-icons/lu";
import Button from "../Button/Button";
import { pathToMapDetail, pathToRobotDetail } from "../../helpers/paths";
import useSort from "../../hooks/useSort";
import ListSearch from "../Form/ListSearch";
import { getPreviousMap, getCurrentMap } from "../../helpers/scheduleHelper";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { useListMapQuery } from "hooks/worker/useMap";
import { CleaningRobotItem } from "types/response/robot";
import { CleaningMapItem } from "types/response/map";
interface CleaningRobotForSchedule extends CleaningRobotItem {
  currentMap?: CleaningMapItem;
  previousMap?: CleaningMapItem;
}

const ScheduleList = () => {
  const { data: robots = [] } = useListRobotQuery();
  const { data: maps = [] } = useListMapQuery();
  // const { data: robots = [] } = useRobots(buildingId);
  // const { data: maps = [] } = useMaps(buildingId);
  const [currentDate, setCurrentDate] = useState<Date>(subDays(new Date(), 0));
  // フィルター前の元データ
  const [currentDateRobots, setCurrentDateRobots] = useState<
    CleaningRobotForSchedule[]
  >([]);
  // フィルター後のデータ
  const [searchedRobots, setSearchedRobots] = useState<
    CleaningRobotForSchedule[]
  >([]);
  const [disabledPrevButton, setDisabledPrevButton] = useState(false);

  // 検索バーに入力された文字
  const [searched, setSearched] = useState("");
  // ソート
  const {
    order,
    orderBy,
    createSortHandler,
    stableSort,
    getComparator,
    orderByGetters,
  } = useSort<CleaningRobotForSchedule>("asc", [(r) => r.robotName]);

  // 前日ボタンを押下したときの動作
  const onClickPrev = () => {
    setCurrentDate(subDays(currentDate, 1));
  };

  // 翌日ボタンを押下したときの動作
  const onClickNext = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  // currentDateと同じ清掃日のロボットのみフィルター
  const robotFilter = useCallback(
    (robot: CleaningRobotForSchedule) => {
      const currentSchedule = robot.schedules.find(
        (s) => s.dayOfWeek === currentDate.getDay()
      );
      return !!currentSchedule && !!currentSchedule.mapId;
    },
    [currentDate]
  );

  // currentDate, robots, robotFilterが変更したときのフィルター適用と前日ボタン非活性化の制御
  useEffect(() => {
    const currentRobots: CleaningRobotForSchedule[] = robots
      .filter(robotFilter)
      .map((r) => {
        const previousMap = getPreviousMap(r.schedules, maps, currentDate);
        const currentMap = getCurrentMap(r.schedules, maps, currentDate);

        return {
          ...r,
          currentMap,
          previousMap,
        };
      })
      .sort((a, b) => {
        const aName = a.previousMap?.mapName || "";
        const bName = b.previousMap?.mapName || "";
        return natsort()(aName, bName);
      });
    setCurrentDateRobots(currentRobots);
    setSearchedRobots(currentRobots);
    setDisabledPrevButton(isToday(currentDate));
  }, [currentDate, maps, robotFilter, robots]);

  // リストが見つからなかったときのメッセージを生成
  const getMessageBySearchedList = (
    searchedList: CleaningRobotItem[],
    searchString: string
  ): string => {
    if (searchedList.length === 0 && searchString.length !== 0) {
      return "検索結果はありません";
    } else if (searchedList.length === 0 && searchString.length === 0) {
      return "清掃予定はありません";
    } else {
      return "";
    }
  };

  return (
    <Layout title="次回清掃予定">
      {" "}
      {/* Layoutコンポーネントを追加し、タイトルを設定 */}
      <Box>
        <Box sx={{ mb: 1 }}>
          <ListSearch<CleaningRobotForSchedule>
            initialRows={currentDateRobots}
            searched={searched}
            setRows={setSearchedRobots}
            setSearched={setSearched}
            columnGetters={[
              (robot) => robot.robotName,
              (robot) => robot.currentMap?.mapName || "",
              (robot) => robot.previousMap?.mapName || "",
            ]}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            alignItems: "center",
          }}
        >
          {/* 前日ボタン */}
          <Button
            variant="outlined"
            startIcon={<ArrowLeftOutlined />}
            onClick={onClickPrev}
            sx={{ whiteSpace: "nowrap" }}
            disabled={disabledPrevButton}
          >
            前日
          </Button>
          {/* currentDate表示 */}
          <Typography variant="h6" sx={{ mx: 1 }}>
            {format(currentDate, "yyyy/MM/dd(E)", { locale: ja })}
          </Typography>
          {/* 翌日ボタン */}
          <Button
            variant="outlined"
            endIcon={<ArrowRightOutlined />}
            onClick={onClickNext}
            sx={{ whiteSpace: "nowrap" }}
          >
            翌日
          </Button>
        </Box>

        {/* フィルターしたロボットが1台以上の場合はテーブルを表示 */}
        {searchedRobots.length > 0 ? (
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 150 }}>
                    <TableSortLabel
                      active={orderBy === "previousMap.name"}
                      direction={orderBy === "previousMap.name" ? order : "asc"}
                      hideSortIcon={true}
                      onClick={createSortHandler("previousMap.name", [
                        (r) => r.previousMap?.mapName || "",
                      ])}
                    >
                      <Box>
                        設定マップ
                        <br />
                        ⇩
                        <br />
                        <Typography
                          sx={{
                            color: (theme) => theme.palette.secondary.main,
                            fontWeight: 600,
                          }}
                        >
                          次回設定マップ
                        </Typography>
                      </Box>
                      {orderBy === "previousMap.name" ? undefined : (
                        <LuArrowUpDown style={{ opacity: 0.3 }} />
                      )}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell style={{ width: 110 }}>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      hideSortIcon={true}
                      onClick={createSortHandler("name", [(r) => r.robotName])}
                    >
                      ロボット
                      {orderBy === "name" ? undefined : (
                        <LuArrowUpDown style={{ opacity: 0.3 }} />
                      )}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {stableSort(
                  searchedRobots,
                  getComparator(order, orderByGetters)
                ).map((r) => {
                  return (
                    <TableRow key={r.robotId}>
                      {/* 前回清掃マップ⇒次回設定マップ */}
                      <TableCell>
                        <Box>
                          <Typography
                            component={Link}
                            variant="inherit"
                            to={pathToMapDetail(r.previousMap?.mapId || "")}
                          >
                            {r.previousMap?.mapName}
                          </Typography>
                        </Box>
                        <Box>⇩</Box>
                        <Box>
                          <Typography
                            sx={{
                              color: (theme) => theme.palette.secondary.main,
                              fontWeight: 600,
                            }}
                            variant="inherit"
                            component={Link}
                            to={pathToMapDetail(r.currentMap?.mapId || "")}
                          >
                            {r.currentMap?.mapName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component={Link}
                          to={pathToRobotDetail(r.robotId)}
                          variant="inherit"
                        >
                          {r.robotName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // フィルターしたロボットが0台の場合の表示
          <Typography variant="h5" sx={{ margin: 4 }}>
            {getMessageBySearchedList(searchedRobots, searched)}
          </Typography>
        )}
      </Box>
    </Layout>
  );
};
export default ScheduleList;
